import { useState } from "react";
import { Plus } from "lucide-react";

import {
  FraudElementCategories,
  FraudElementInput,
} from "../../../api/fraudElements";
import {
  useAppDispatch,
} from "../../../app/hooks";
import ManagementModal from "../../../components/ui/modals/ManagementModal";
import { createFraudElements } from "../logic/fraudElementsThunk";
import FraudElementForm from "./FraudElementFormData";
import { useParams } from "react-router-dom";
import { showErrorToast, showSuccessToast } from "../../../lib/toast";

const EMPTY_FORM: FraudElementInput = {
  party_id: "",
  fraud_element_category: null as unknown as FraudElementCategories,
  details: undefined,
};

interface CreateFraudElementsModalProps {
  onClose: () => void;
}

export default function CreateFraudElementsModal({
  onClose,
}: CreateFraudElementsModalProps) {
  const dispatch = useAppDispatch();

 const {id: caseId} = useParams<{ id: string }>();

  const [isLoading, setIsLoading] = useState(false);
  const [fraudElements, setFraudElements] = useState<FraudElementInput[]>([EMPTY_FORM]);

  const updateFraudElement = < K extends keyof FraudElementInput>(
    index: number,
    field: K,
    value: FraudElementInput[K],
  ) => {
    setFraudElements((previous) =>
      previous.map((fraudElement, i) =>
        i === index
          ? {
              ...fraudElement,
              [field]: value,
            }
          : fraudElement,
      ),
    );
  };

  const addFraudElement = () =>
    setFraudElements((previous) => [
      ...previous,
      { ...EMPTY_FORM },
    ]);

  const removeFraudElement = (index: number) =>
    setFraudElements((previous) =>
      previous.filter((_, i) => i !== index),
    );

  const isFormValid = fraudElements.length > 0 && fraudElements.every(
    (element) =>
      element.party_id !== "" &&
      element.fraud_element_category !== null &&
      element.fraud_element_category !== undefined
  );

  const handleSubmit = async () => {
    
    if (!caseId || !isFormValid || isLoading) {
      return;
    }

    setIsLoading(true);
    try {
      await dispatch(
        createFraudElements({
          case_id: caseId,
          request: {
            fraud_elements: fraudElements,
          },
        }),
      );
      showSuccessToast("Fraud Elements created successfully");
      onClose();
    } catch (error) {
      showErrorToast("Failed to create fraud elements. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <ManagementModal
      title="Create Fraud Elements"
      submitLabel="Create Fraud Element"
      submitEnabled={isFormValid && !isLoading}
      isLoading={isLoading}
      onSubmit={handleSubmit}
      onClose={onClose}
      onBack={onClose}
    >
      <div className="flex flex-col gap-1">
        <div className="flex flex-col gap-3">
          {fraudElements.map((fraudElement, index) => (
            <FraudElementForm
              key={index}
              index={index}
              fraudElement={fraudElement}
              onDelete={() => removeFraudElement(index)}
              onChange={(field, value) =>
                updateFraudElement(index, field, value)
              }
            />
          ))}
        </div>

        <button
          type="button"
          onClick={addFraudElement}
          disabled={isLoading}
          className="flex h-9 w-fit items-center justify-center gap-2 rounded-[8px] bg-transparent p-2 text-[14px] font-normal leading-5 text-[#737373] transition-colors disabled:opacity-50 disabled:pointer-events-none"
        >
          <Plus className="h-4 w-4" />
          Add New Fraud Element
        </button>
      </div>
    </ManagementModal>
  );
}